<template>
  <div class="mod-page">
    <!-- Header -->
    <div class="mod-header">
      <div>
        <h1 class="mod-title">Material por Almacén</h1>
        <p class="mod-subtitle">Existencias de materiales por almacén</p>
      </div>
      <div class="mod-header-actions">
        <button class="mod-btn mod-btn-outline" @click="exportExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span>Exportar</span>
        </button>
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nuevo registro</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="mod-filters">
      <div class="mod-search">
        <i class="bi bi-search"></i>
        <input
          type="text"
          v-model="searchText"
          placeholder="Buscar por material o almacén..."
          @input="applyFilters"
        >
      </div>
      <div class="mod-filter-right">
        <select v-model="pageSize" @change="applyFilters" class="mod-select">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- Tabla -->
    <div class="mod-table-wrap">
      <table class="mod-table">
        <thead>
          <tr>
            <th class="mod-th-sort" @click="sortBy('MaterialClave')">
              Material
              <i :class="sortIcon('MaterialClave')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('AlmacenClave')">
              Almacén
              <i :class="sortIcon('AlmacenClave')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('EnStock')">
              En Stock
              <i :class="sortIcon('EnStock')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('Comprometido')">
              Comprometido
              <i :class="sortIcon('Comprometido')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('Pedido')">
              Pedido
              <i :class="sortIcon('Pedido')"></i>
            </th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td colspan="6" class="mod-td-center mod-td-empty">
              <i class="bi bi-inbox"></i>
              <span>{{ searchText ? 'Sin resultados' : 'No hay registros de material por almacén' }}</span>
            </td>
          </tr>
          <tr v-for="item in pagedItems" :key="item.MaterialClave + '-' + item.AlmacenClave + '-' + item.EmpresaId" class="mod-tr">
            <td><span class="mod-badge">{{ item.MaterialClave }}</span></td>
            <td><span class="mod-badge">{{ item.AlmacenClave }}</span></td>
            <td>{{ item.EnStock }}</td>
            <td>{{ item.Comprometido }}</td>
            <td>{{ item.Pedido }}</td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginacion -->
    <div class="mod-pagination" v-if="totalPages > 1">
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
        <i class="bi bi-chevron-double-left"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="bi bi-chevron-left"></i>
      </button>
      <template v-for="p in visiblePages" :key="p">
        <button
          class="mod-page-btn"
          :class="{ 'mod-page-active': p === currentPage }"
          @click="currentPage = p"
        >{{ p }}</button>
      </template>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
        <i class="bi bi-chevron-double-right"></i>
      </button>
      <span class="mod-page-info">Pagina {{ currentPage }} de {{ totalPages }}</span>
    </div>

    <!-- Modal Form -->
    <MaterialAlmacenForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import materialalmacenService from './materialalmacen.service';
import authService from '@/services/auth';
import MaterialAlmacenForm from './MaterialAlmacenForm.vue';

export default {
  name: 'MaterialAlmacenList',
  components: { MaterialAlmacenForm },
  data() {
    return {
      items: [],
      filteredItems: [],
      searchText: '',
      loading: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      // Paginacion
      currentPage: 1,
      pageSize: 25,
      // Ordenamiento
      sortField: 'MaterialClave',
      sortAsc: true
    };
  },
  computed: {
    totalFiltered() {
      return this.filteredItems.length;
    },
    totalPages() {
      return Math.ceil(this.totalFiltered / this.pageSize) || 1;
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    currentPage(val) {
      if (val > this.totalPages) this.currentPage = this.totalPages;
    }
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        const result = await materialalmacenService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
        if (Array.isArray(result)) {
          this.items = result;
        } else if (result.OData) {
          this.items = result.OData;
        } else {
          this.items = [];
        }
        this.applyFilters();
      } catch (e) {
        console.error('Error al cargar material por almacén:', e);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      let data = [...this.items];

      // Busqueda
      const q = this.searchText.toLowerCase().trim();
      if (q) {
        data = data.filter(i =>
          (i.MaterialClave || '').toLowerCase().includes(q) ||
          (i.AlmacenClave || '').toLowerCase().includes(q)
        );
      }

      // Ordenamiento
      data.sort((a, b) => {
        let va = a[this.sortField];
        let vb = b[this.sortField];
        if (va == null) va = '';
        if (vb == null) vb = '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });

      this.filteredItems = data;
      this.currentPage = 1;
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortField = field;
        this.sortAsc = true;
      }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved() {
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`Eliminar registro "${item.MaterialClave} - ${item.AlmacenClave}"?`)) return;
      try {
        const result = await materialalmacenService.borrar(item);
        if (result.Number >= 0) {
          this.loadItems();
        } else {
          alert(result.Message || 'Error al eliminar');
        }
      } catch (e) {
        console.error('Error al eliminar:', e);
        alert('Error al eliminar');
      }
    },
    exportExcel() {
      const data = this.filteredItems.map(i => ({
        Material: i.MaterialClave,
        'Almacén': i.AlmacenClave,
        'En Stock': i.EnStock,
        Comprometido: i.Comprometido,
        Pedido: i.Pedido
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      // Auto-width
      const colWidths = Object.keys(data[0] || {}).map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'MaterialAlmacen');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'material_almacen.xlsx');
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";
</style>
